/**
 * EmbedPreview Component
 * Module 07 - Dashboard UI
 * Section 06 - Embeds Management (Step 6.5)
 * 
 * Live preview of embed iframe with loading and error states
 */

import React, { useState, useEffect, useMemo } from 'react';
import { AlertCircle, ExternalLink } from 'lucide-react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import LoadingSpinner from '../common/LoadingSpinner';

interface EmbedPreviewProps {
  open: boolean;
  onClose: () => void;
  embedId: string;
  embedToken: string;
  embedName?: string;
  viewerUrl?: string;
}

/**
 * EmbedPreview shows a live preview of the embed iframe
 * Simulates how the embed will appear on a website
 */
const EmbedPreview: React.FC<EmbedPreviewProps> = ({
  open,
  onClose,
  embedId,
  embedToken,
  embedName = 'Embed',
  viewerUrl = import.meta.env.VITE_EMBED_BASE_URL,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Build iframe URL — CDN has no server-side routing so we use index.html with query params
  const iframeUrl = useMemo(() => {
    try {
      const base = (viewerUrl || '').replace(/\/$/, '');
      const url = new URL(`${base}/index.html`);
      url.searchParams.set('embedId', embedId);
      url.searchParams.set('token', embedToken);
      // Pass the dashboard origin so the embed SPA can forward it as X-Parent-Origin
      // to the API. This lets the domain check validate correctly and ensures
      // the owner can always preview their embeds from app.opshn.co.uk.
      url.searchParams.set('parentOrigin', window.location.origin);
      return url.toString();
    } catch {
      return '';
    }
  }, [embedId, embedToken, viewerUrl]);

  /**
   * Reset loading state when modal opens
   */
  useEffect(() => {
    if (open) {
      setIsLoading(true);
      setHasError(false);
      setErrorMessage('');
    }
  }, [open]);

  /**
   * Handle iframe load
   */
  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  /**
   * Handle iframe error
   */
  const handleIframeError = () => {
    setIsLoading(false);
    setHasError(true);
    setErrorMessage('Failed to load embed preview. Please check your configuration.');
  };

  /**
   * Open in new window
   */
  const openInNewWindow = () => {
    window.open(iframeUrl, '_blank', 'width=1200,height=800');
  };

  return (
    <Modal 
      open={open} 
      onClose={onClose} 
      title={`Preview: ${embedName}`} 
      size="large"
    >
      <div className="space-y-4">
        {/* Preview Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-800">
            <strong>💡 Preview Mode:</strong> This is how your embed will appear on your website. 
            Interact with the 3D model to test functionality.
          </p>
        </div>

        {/* Preview Container */}
        <div className="relative bg-gray-100 rounded-lg overflow-hidden" style={{ height: '600px' }}>
          {/* Loading State */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
              <div className="text-center">
                <LoadingSpinner size="large" />
                <p className="mt-4 text-sm text-gray-600">Loading 3D viewer...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {hasError && (
            <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
              <div className="text-center max-w-md px-4">
                <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Preview Unavailable
                </h3>
                <p className="text-sm text-gray-600 mb-4">{errorMessage}</p>
                <div className="flex gap-2 justify-center">
                  <Button variant="secondary" onClick={onClose}>
                    Close
                  </Button>
                  <Button variant="primary" onClick={() => window.location.reload()}>
                    Retry
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Iframe */}
          {!hasError && iframeUrl && (
            <iframe
              src={iframeUrl}
              title={`Preview: ${embedName}`}
              className="w-full h-full border-0"
              onLoad={handleIframeLoad}
              onError={handleIframeError}
              sandbox="allow-scripts allow-same-origin allow-forms"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            />
          )}
        </div>

        {/* Preview Controls */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            <p>Use your mouse to interact with the 3D model</p>
            <p className="text-xs text-gray-500 mt-1">
              • Drag to rotate • Scroll to zoom • Right-click to pan
            </p>
          </div>
          
          <Button
            variant="secondary"
            size="sm"
            onClick={openInNewWindow}
          >
            <ExternalLink className="h-4 w-4 mr-1" />
            Open in New Window
          </Button>
        </div>

        {/* Device Preview Options */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 mb-2">Responsive Preview</h4>
          <p className="text-sm text-gray-600 mb-3">
            Test how your embed looks on different screen sizes:
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                // In a real implementation, this would resize the preview
                alert('Desktop view (1920x1080)');
              }}
            >
              Desktop
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                alert('Tablet view (768x1024)');
              }}
            >
              Tablet
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                alert('Mobile view (375x667)');
              }}
            >
              Mobile
            </Button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>
            Close Preview
          </Button>
          <Button variant="primary" onClick={openInNewWindow}>
            <ExternalLink className="h-4 w-4 mr-2" />
            Full Screen
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EmbedPreview;
